import type { AssistantLaterSide, AssistantThreadStatus, SlackFindingConfidence, SlackFindingImportance, SlackPriorVerdict } from './enums.js';

// Why (owner rulings A1, A2, A7 of 2026-09-27; design §3): what a brief keeps, as the store holds it —
// sealed when it drew on Slack findings, plain JSON otherwise (migration 0012). Every reference is an id
// of Calis's own making (a meeting, one of its to-dos, one of its Slack findings, a daily Slack sweep's
// finding), never a quote, a
// permalink, an address or a Slack id; the validator that builds a payload holds every string to the
// egress rules the Slack check's findings are held to. `version` is bumped when the shape changes so a
// reader can refuse an older row rather than misread it.
//
// Version 2 (owner ruling A13) adds `priorReview` and `comparedWith`: what this brief judged of the
// brief before it. A version-1 row is still opened — the store reads it as version 2 with neither
// (upgradeAssistantBriefPayload) — so a brief written before the ruling stays readable until the next
// brief replaces it.
//
// Version 3 (owner request of 2026-09-30, the daily Slack sweep; rulings Q1, Q4): a reference may name
// one of the daily sweep's findings — an open matter it found in Slack, or what it showed of one of the
// last brief's open items — by the sweep's own id (`sweep_finding`), belonging to no meeting. Its shape
// is otherwise version 2's, so a version-1 or version-2 row is read as version 3 unchanged (it simply
// cites no sweep finding); the number moves so that an older build, which would not know where such a
// reference leads, refuses a version-3 row rather than misread it.

export const ASSISTANT_BRIEF_PAYLOAD_VERSION = 3;
/** Versions a reader still opens; anything else is unreadable. */
const READABLE_VERSIONS: readonly number[] = [1, 2, ASSISTANT_BRIEF_PAYLOAD_VERSION];

/**
 * What a sentence of the brief rests on: a meeting, one of its to-dos, one of its Slack findings, or one of
 * the daily sweep's findings (ids only). A sweep finding belongs to no meeting: it is a matter the sweep
 * found in the person's Slack, or its update on an open item of the last brief, shown on the Assistant's
 * "Slack today" tab.
 */
export type AssistantBriefRef =
  | { type: 'meeting'; meetingId: string }
  | { type: 'todo'; meetingId: string | null; todoId: string }
  | { type: 'finding'; meetingId: string; findingId: string }
  | { type: 'sweep_finding'; findingId: string };

/** The meeting a reference is about, when it is about one (a sweep finding is about the person's day). */
export function briefRefMeetingId(ref: AssistantBriefRef): string | null {
  return ref.type === 'sweep_finding' ? null : ref.meetingId;
}

/** An ongoing matter across meetings. */
export interface AssistantBriefThread {
  title: string;
  status: AssistantThreadStatus;
  summary: string;
  nextStep: string | null;
  refs: AssistantBriefRef[];
}

/** Something the person owes, or is owed (names as the meetings gave them; dates as YYYY-MM-DD). */
export interface AssistantBriefCommitment {
  text: string;
  who: string | null;
  due: string | null;
  overdue: boolean;
  refs: AssistantBriefRef[];
}

/** Two things that do not agree — meeting against meeting, or a meeting against one of its Slack findings. */
export interface AssistantBriefInconsistency {
  title: string;
  sideA: string;
  refsA: AssistantBriefRef[];
  sideB: string;
  refsB: AssistantBriefRef[];
  whichIsLater: AssistantLaterSide;
  importance: SlackFindingImportance;
  confidence: SlackFindingConfidence;
}

export interface AssistantBriefWatchOut {
  text: string;
  refs: AssistantBriefRef[];
}

export interface AssistantBriefPerson {
  name: string;
  why: string;
  refs: AssistantBriefRef[];
}

/**
 * What a verdict judged, copied from the previous brief so it can be shown after that brief is gone
 * (completing a brief removes the one before it): the kind of entry and its words, never its ids.
 */
export type AssistantBriefPriorSubject =
  | { type: 'thread'; title: string }
  | { type: 'commitment'; side: 'you_owe' | 'owed_to_you'; text: string }
  | { type: 'inconsistency'; title: string }
  | { type: 'watch_out'; text: string }
  /** One of the previous brief's action items, as it stands on the person's list. */
  | { type: 'action_item'; todoId: string; text: string };

/** One verdict on the previous brief (owner ruling A13), as the payload keeps it. */
export interface AssistantBriefPriorReview {
  /** `B2`, `BC1`, `BI1`, `BW1`, `BT1` in the prompt that judged it. */
  ref: string;
  verdict: SlackPriorVerdict;
  reason: string;
  /** What shows it: ids of this brief's material; always some for a changed verdict, any cited for the rest. */
  refs: AssistantBriefRef[];
  prior: AssistantBriefPriorSubject;
}

/** The brief whose entries were judged, and how many of this brief's entries were not among its own. */
export interface AssistantBriefComparedWith {
  /** When the previous brief was written. */
  completedAt: string;
  /** Entries of this brief (threads, commitments, inconsistencies, watch-outs) the previous brief did not have. */
  newEntries: number;
}

/** The ids of the to-dos a payload refers to, each once, in reading order. */
export function briefTodoRefIds(payload: Pick<AssistantBriefPayload, 'threads' | 'commitments' | 'inconsistencies' | 'watchOuts' | 'people'> & Partial<Pick<AssistantBriefPayload, 'priorReview'>>): string[] {
  const refs: AssistantBriefRef[] = [
    ...payload.threads.flatMap((thread) => thread.refs),
    ...payload.commitments.youOwe.flatMap((item) => item.refs),
    ...payload.commitments.owedToYou.flatMap((item) => item.refs),
    ...payload.inconsistencies.flatMap((item) => [...item.refsA, ...item.refsB]),
    ...payload.watchOuts.flatMap((item) => item.refs),
    ...payload.people.flatMap((person) => person.refs),
    ...(payload.priorReview ?? []).flatMap((entry) => entry.refs),
  ];
  const ids = refs.flatMap((ref) => (ref.type === 'todo' ? [ref.todoId] : []));
  for (const entry of payload.priorReview ?? []) if (entry.prior.type === 'action_item') ids.push(entry.prior.todoId);
  return [...new Set(ids)];
}

export interface AssistantBriefPayload {
  version: typeof ASSISTANT_BRIEF_PAYLOAD_VERSION;
  /** One or two sentences: how things are going overall. */
  headline: string;
  /** What the brief was given: how many meetings, and how many the character budget left out (oldest first). */
  coverage: { meetingsConsidered: number; meetingsDropped: number };
  threads: AssistantBriefThread[];
  commitments: { youOwe: AssistantBriefCommitment[]; owedToYou: AssistantBriefCommitment[] };
  inconsistencies: AssistantBriefInconsistency[];
  watchOuts: AssistantBriefWatchOut[];
  people: AssistantBriefPerson[];
  /** The verdicts on the previous brief (owner ruling A13); null when there was no previous brief to judge. */
  priorReview: AssistantBriefPriorReview[] | null;
  comparedWith: AssistantBriefComparedWith | null;
}

/** A stored payload this build still opens, as it was written (upgradeAssistantBriefPayload brings it to this version). */
export type StoredAssistantBriefPayload =
  | AssistantBriefPayload
  | (Omit<AssistantBriefPayload, 'version'> & { version: 2 })
  | (Omit<AssistantBriefPayload, 'version' | 'priorReview' | 'comparedWith'> & { version: 1 });

/** The shape check a stored brief must pass before it is trusted as a payload (the store's read): version 1, 2 or 3. */
export function isAssistantBriefPayload(value: unknown): value is StoredAssistantBriefPayload {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  const commitments = candidate.commitments as Record<string, unknown> | undefined;
  const coverage = candidate.coverage as Record<string, unknown> | undefined;
  const base =
    typeof candidate.version === 'number' &&
    READABLE_VERSIONS.includes(candidate.version) &&
    typeof candidate.headline === 'string' &&
    typeof coverage === 'object' &&
    coverage !== null &&
    typeof coverage.meetingsConsidered === 'number' &&
    typeof coverage.meetingsDropped === 'number' &&
    Array.isArray(candidate.threads) &&
    typeof commitments === 'object' &&
    commitments !== null &&
    Array.isArray(commitments.youOwe) &&
    Array.isArray(commitments.owedToYou) &&
    Array.isArray(candidate.inconsistencies) &&
    Array.isArray(candidate.watchOuts) &&
    Array.isArray(candidate.people);
  if (!base) return false;
  if (candidate.version === 1) return true;
  const comparedWith = candidate.comparedWith as Record<string, unknown> | null | undefined;
  return (
    (candidate.priorReview === null || Array.isArray(candidate.priorReview)) &&
    (comparedWith === null || (typeof comparedWith === 'object' && comparedWith !== null && typeof comparedWith.completedAt === 'string' && typeof comparedWith.newEntries === 'number'))
  );
}

/**
 * A readable payload as this build holds it: a version-1 row judged nothing of any earlier brief; a version-2
 * row is a version-3 one that cites no sweep finding.
 */
export function upgradeAssistantBriefPayload(payload: StoredAssistantBriefPayload): AssistantBriefPayload {
  if (payload.version === ASSISTANT_BRIEF_PAYLOAD_VERSION) return payload;
  if (payload.version === 2) return { ...payload, version: ASSISTANT_BRIEF_PAYLOAD_VERSION };
  return { ...payload, version: ASSISTANT_BRIEF_PAYLOAD_VERSION, priorReview: null, comparedWith: null };
}
